import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button.js';
import { toast } from '../ui/Toast.js';

interface PreJoinScreenProps {
  slug: string;
  displayName: string;
  onJoin: (prefs: { audio: boolean; video: boolean }) => void;
  onCancel: () => void;
}

export function PreJoinScreen({ slug, displayName, onJoin, onCancel }: PreJoinScreenProps) {
  const [micEnabled, setMicEnabled] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Attach stream to video element whenever stream changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      cameraStream?.getTracks().forEach((t) => t.stop());
    };
  }, [cameraStream]);

  async function handleToggleMic() {
    if (micEnabled) {
      setMicEnabled(false);
      return;
    }
    try {
      // Request permission — stop tracks immediately (no audio preview needed)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicEnabled(true);
    } catch {
      toast.error('Microphone access denied. You can still join without it.');
    }
  }

  async function handleToggleCamera() {
    if (cameraEnabled) {
      cameraStream?.getTracks().forEach((t) => t.stop());
      setCameraStream(null);
      setCameraEnabled(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      setCameraEnabled(true);
    } catch {
      toast.error('Camera access denied. You can still join without it.');
    }
  }

  function handleJoin() {
    onJoin({ audio: micEnabled, video: cameraEnabled });
  }

  const toggleClass = (on: boolean) =>
    `relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
     transition-colors duration-200 ease-in-out focus:outline-none
     ${on ? 'bg-ember-500' : 'bg-neutral-700'}`;

  const knobClass = (on: boolean) =>
    `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow
     transition duration-200 ease-in-out ${on ? 'translate-x-5' : 'translate-x-0'}`;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-950 px-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center">
          <span className="text-3xl">🔥</span>
          <h1 className="mt-3 text-2xl font-bold text-neutral-100">Ready to join?</h1>
          <p className="mt-1 text-sm text-neutral-400">
            Room: <span className="font-mono text-neutral-300">{slug}</span>
          </p>
        </div>

        {/* Camera preview */}
        <div className="relative mx-auto flex h-52 w-full max-w-sm items-center justify-center overflow-hidden rounded-xl bg-neutral-800">
          {cameraEnabled && cameraStream ? (
            <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-cover scale-x-[-1]" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-neutral-500">
              <span className="text-5xl">👤</span>
              <p className="text-sm">{displayName}</p>
            </div>
          )}
          <div className="absolute bottom-2 left-2 flex gap-2">
            <span className={`rounded-full px-2 py-0.5 text-xs ${micEnabled ? 'bg-neutral-700/80 text-neutral-200' : 'bg-red-900/80 text-red-300'}`}>
              {micEnabled ? '🎙️ Mic on' : '🔇 Mic off'}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs ${cameraEnabled ? 'bg-neutral-700/80 text-neutral-200' : 'bg-red-900/80 text-red-300'}`}>
              {cameraEnabled ? '📸 Camera on' : '📷 Camera off'}
            </span>
          </div>
        </div>

        {/* Device toggles */}
        <div className="rounded-xl border border-neutral-800 bg-neutral-900 divide-y divide-neutral-800">
          {/* Microphone row */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-xl">{micEnabled ? '🎙️' : '🔇'}</span>
              <div>
                <p className="text-sm font-medium text-neutral-100">Microphone</p>
                <p className="text-xs text-neutral-500">{micEnabled ? 'Your audio will be shared' : 'You will join muted'}</p>
              </div>
            </div>
            <button onClick={() => { void handleToggleMic(); }} className={toggleClass(micEnabled)} role="switch" aria-checked={micEnabled}>
              <span className={knobClass(micEnabled)} />
            </button>
          </div>
          {/* Camera row */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-xl">{cameraEnabled ? '📸' : '📷'}</span>
              <div>
                <p className="text-sm font-medium text-neutral-100">Camera</p>
                <p className="text-xs text-neutral-500">{cameraEnabled ? 'Your video will be shared' : 'You will join with camera off'}</p>
              </div>
            </div>
            <button onClick={() => { void handleToggleCamera(); }} className={toggleClass(cameraEnabled)} role="switch" aria-checked={cameraEnabled}>
              <span className={knobClass(cameraEnabled)} />
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button variant="ghost" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button className="flex-1" onClick={handleJoin}>Join now →</Button>
        </div>

        <p className="text-center text-xs text-neutral-600">🔒 End-to-end encrypted · emberchat.link</p>
      </div>
    </div>
  );
}
